import { useState } from 'react';

import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useClassificationExecutions, useQualityExecutions } from '@/hooks/useAgentExecutions';
import { usePageTitle } from '@/hooks/usePageTitle';

const PAGE_SIZE = 25;

const CHANNEL_LABEL: Record<string, string> = {
  PSTN: 'Phone',
  WEBRTC: 'Web',
  WEB_WIDGET: 'Web',
  WHATSAPP: 'WhatsApp',
  EMAIL: 'Email',
};

function trueFlags(record: Record<string, unknown>): string[] {
  return Object.entries(record)
    .filter(([, v]) => v === true)
    .map(([k]) => k);
}

function FlagBadges({ record }: { record: Record<string, unknown> }) {
  const flags = trueFlags(record);
  if (flags.length === 0) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  const shown = flags.slice(0, 3);
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((f) => (
        <Badge key={f} variant="secondary" className="font-mono text-[10px]">
          {f}
        </Badge>
      ))}
      {flags.length > shown.length && (
        <Badge variant="outline" className="text-[10px]">
          +{flags.length - shown.length}
        </Badge>
      )}
    </div>
  );
}

export function AgentExecutionsListPage({ kind }: { kind: 'quality' | 'classification' }) {
  const isQuality = kind === 'quality';
  usePageTitle(isQuality ? 'Quality Executions' : 'Classifier Executions');
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const qualityQuery = useQualityExecutions(isQuality ? page : 1, PAGE_SIZE);
  const classifyQuery = useClassificationExecutions(!isQuality ? page : 1, PAGE_SIZE);
  const { data, isLoading } = isQuality ? qualityQuery : classifyQuery;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const basePath = isQuality ? '/quality-executions' : '/classifier-executions';

  if (isLoading && !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>{isQuality ? 'Quality Executions' : 'Classifier Executions'}</CardTitle>
            <CardDescription>
              {isQuality
                ? 'Every QualityAgent run, newest first. Click a row for full details including the exact model input.'
                : 'Every ClassifierAgent run, newest first. Click a row for full details including the exact model input.'}
            </CardDescription>
          </div>
          <div className="text-muted-foreground text-xs whitespace-nowrap">
            {data?.total ?? 0} total
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Call</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead>Run</TableHead>
                <TableHead>Config</TableHead>
                <TableHead>{isQuality ? 'Flags' : 'Labels'}</TableHead>
                {isQuality && <TableHead>Severity</TableHead>}
                <TableHead>Conf.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.items ?? []).map((row) => {
                const flags = isQuality
                  ? (row as { flags: Record<string, unknown> }).flags
                  : (row as { labels: Record<string, unknown> }).labels;
                const when = isQuality
                  ? (row as { assessed_at: string }).assessed_at
                  : (row as { classified_at: string }).classified_at;
                const config = isQuality
                  ? (row as { quality_config: { name: string } | null }).quality_config
                  : (row as { classifier_config: { name: string } | null }).classifier_config;
                const severity =
                  isQuality && typeof row.data?.severity === 'string' ? row.data.severity : null;
                return (
                  <TableRow
                    key={`${row.call_id}:${row.name}`}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(`${basePath}/${row.call_id}/${encodeURIComponent(row.name)}`)
                    }
                  >
                    <TableCell className="text-xs whitespace-nowrap">
                      {new Date(when).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-xs">{row.call?.phone_number ?? '—'}</TableCell>
                    <TableCell className="text-xs">
                      {CHANNEL_LABEL[row.call?.call_transport ?? ''] ??
                        row.call?.call_transport ??
                        '—'}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{row.name}</TableCell>
                    <TableCell className="text-xs">
                      {config?.name ?? <span className="text-muted-foreground">agent node</span>}
                    </TableCell>
                    <TableCell>
                      <FlagBadges record={flags} />
                    </TableCell>
                    {isQuality && (
                      <TableCell className="text-xs capitalize">
                        {severity ?? <span className="text-muted-foreground">—</span>}
                      </TableCell>
                    )}
                    <TableCell className="text-muted-foreground text-xs">
                      {row.confidence_score != null
                        ? `${Math.round(row.confidence_score * 100)}%`
                        : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
              {(data?.items ?? []).length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={isQuality ? 8 : 7}
                    className="text-muted-foreground py-8 text-center text-sm"
                  >
                    No executions yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="mt-4 flex items-center justify-end gap-2">
            <span className="text-muted-foreground text-xs">
              Page {data?.page ?? page} of {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
